import { Edit as PencilIcon } from 'education-icons';
import { Input, Text } from 'platform-components';
import { INPUT_SIZES, INPUT_THEMES, KEY_CODES } from 'platform-components/constants';
import { InputKeyPressEvent, InputChangeEvent } from 'platform-components/src/typings';
import React, { useState, useEffect } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';

import lessonActions from 'common.components/lesson/actions';
import { LessonStore } from 'common.components/lesson/typings';

import * as selectors from './selectors';

import './lesson-view__description.scss';

export const DESCRIPTION_MAX_LENGTH = 180;

interface StoreProps {
    lessonDescription: string;
    isDescriptionEmpty: boolean;
}

interface DispatchProps {
    saveLesson: typeof lessonActions.saveLesson;
    setLessonDescription: typeof lessonActions.setDescription;
}

interface Props extends StoreProps, DispatchProps, WrappedComponentProps {
    canEdit: boolean;
}

export const LessonDescription = (props: Props) => {
    const {
        canEdit,
        lessonDescription: savedLessonDescription,
        setLessonDescription,
        saveLesson,
        isDescriptionEmpty: isSavedDescriptionEmpty,
    } = props;

    const [isEditing, setIsEditing] = useState(false);
    const [description, setDescription] = useState(savedLessonDescription);
    const descriptionIsCleared = savedLessonDescription !== description && isSavedDescriptionEmpty && !isEditing;

    useEffect(() => {
        setDescription(savedLessonDescription);
    }, [savedLessonDescription, descriptionIsCleared]);

    const isDescriptionValid = () => description.trim().length < DESCRIPTION_MAX_LENGTH;

    const handleClickIcon = () => {
        setIsEditing(true);
    };

    const saveDescription = () => {
        if (isDescriptionValid()) {
            setIsEditing(false);
            setLessonDescription({ description: description.trim() });
            saveLesson();
        }
    };

    const handleChangeInput = ({ target: { value } }: InputChangeEvent) => {
        setDescription(value);
    };

    const handleTitleEnterPress = ({ keyCode }: InputKeyPressEvent) => {
        if (keyCode === KEY_CODES.ENTER) {
            saveDescription();
        }
    };

    const setTitleInputRef = (ref: HTMLInputElement) => {
        if (ref) {
            // таймаут нужен, чтобы инпут успел отрендериться и зафокуситься
            setTimeout(() => {
                ref.setSelectionRange(0, description?.length || 0);
            }, 50);
        }
    };

    if (isEditing) {
        return (
            <div className="lesson-view__description">
                <Input
                    focusInputEnd
                    focused
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    mix="lesson-view__description-input"
                    onBlur={saveDescription}
                    onChange={handleChangeInput}
                    onControlRef={setTitleInputRef}
                    onKeyUp={handleTitleEnterPress}
                    size={INPUT_SIZES.M}
                    theme={INPUT_THEMES.YANDEX}
                    valid={isDescriptionValid() ? 'uncertain' : 'no'}
                    value={description}
                />
            </div>
        );
    }

    if (!description && !canEdit) {
        return null;
    }

    return (
        <div className="lesson-view__description">
            <Text size={Text.SIZE.L}>{description}</Text>
            {canEdit && (
                <span className="lesson-view__description-edit">
                    <PencilIcon className="lesson-view__description-edit-icon" onClick={handleClickIcon} />
                </span>
            )}
        </div>
    );
};

const mapStateToProps = <T extends LessonStore>(storeState: T, { intl }: WrappedComponentProps): StoreProps => {
    return {
        lessonDescription: selectors.getLessonDescriptionOrDefault(storeState, { intl }),
        isDescriptionEmpty: selectors.getIsDescriptionEmpty(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            saveLesson: lessonActions.saveLesson,
            setLessonDescription: lessonActions.setDescription,
        },
        dispatch
    );
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(LessonDescription));
